// src/pages/LeaderboardPage.js
import React, { useState, useEffect } from "react";
import { collection, query, orderBy, limit, onSnapshot, where, Timestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { computeLevel, ONI_IMG_VERSION, ONI_FORMS } from "../data/demons";
import EmptyState from "../components/EmptyState";
import "./LeaderboardPage.css";

function getWeekStart() {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(now.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(monday);
}

function RankBadge({ rank }) {
  if (rank === 1) return <span className="lb-rank gold">🥇</span>;
  if (rank === 2) return <span className="lb-rank silver">🥈</span>;
  if (rank === 3) return <span className="lb-rank bronze">🥉</span>;
  return <span className="lb-rank-num">#{rank}</span>;
}

function OniAvatar({ oni, size = 48 }) {
  if (oni?.image) {
    return (
      <div className="lb-oni-avatar" style={{ width: size, height: size }}>
        <img src={`${oni.image}?v=${ONI_IMG_VERSION}`} alt={oni.name || "Oni"} className="lb-oni-avatar-img" />
      </div>
    );
  }
  return <span className="lb-oni-emoji" style={{ fontSize: size * 0.55 }}>{oni?.emoji || "👹"}</span>;
}

function PlayerRow({ player, rank, type, isMe }) {
  const level = computeLevel(player.xp || 0);
  const score = type === "xp" ? player.xp : player.weeklyDuelsWon;
  const label = type === "xp" ? "XP" : "duels";

  return (
    <div className={`lb-row${isMe ? " lb-row-me" : ""}${rank <= 3 ? " lb-row-top" : ""}`}>
      <div className="lb-rank-col">
        <RankBadge rank={rank} />
      </div>
      <div className="lb-player-col">
        <OniAvatar oni={player.demon?.oni} size={44} />
        <div className="lb-player-info">
          <p className="lb-player-name">{player.displayName || "Oni Inconnu"}{isMe ? " (moi)" : ""}</p>
          <p className="lb-player-oni">{player.demon?.oni?.name || "—"} · {level.current.badge} LV.{level.current.level}</p>
        </div>
      </div>
      <div className="lb-score-col">
        <span className="lb-score-val">{score || 0}</span>
        <span className="lb-score-label">{label}</span>
      </div>
    </div>
  );
}

export default function LeaderboardPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("xp");
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekLabel, setWeekLabel] = useState("");

  useEffect(() => {
    const now = new Date();
    const opts = { day:"numeric", month:"long" };
    const start = new Date(); start.setDate(now.getDate() - now.getDay() + 1);
    const end   = new Date(start); end.setDate(start.getDate() + 6);
    setWeekLabel(`${start.toLocaleDateString("fr-FR", opts)} — ${end.toLocaleDateString("fr-FR", opts)}`);
  }, []);

  useEffect(() => {
    setLoading(true);
    const field = tab === "xp" ? "xp" : "weeklyDuelsWon";
    const q = query(
      collection(db, "users"),
      orderBy(field, "desc"),
      limit(50)
    );
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs
        .map(d => {
          const p = { id: d.id, ...d.data() };
          if (p.demon?.oni?.id) {
            const fresh = ONI_FORMS.find(o => o.id === p.demon.oni.id);
            if (fresh) p.demon = { ...p.demon, oni: { ...fresh, ...p.demon.oni, image: fresh.image, emoji: fresh.emoji } };
          }
          return p;
        })
        .filter(p => (p[field] || 0) > 0);
      setPlayers(data);
      setLoading(false);
    });
    return unsub;
  }, [tab]);

  return (
    <div className="lb-page">
      <div className="section" style={{ paddingTop: "7rem" }}>

        <div className="lb-header">
          <div>
            <span className="section-eyebrow">番付 · Classement</span>
            <h1 className="section-title">Leaderboard<br/>Démoniaque</h1>
            <div className="divider" />
          </div>
          <div className="lb-week-badge">
            <span className="lb-week-label">SEMAINE EN COURS</span>
            <span className="lb-week-dates">{weekLabel}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="lb-tabs">
          <button className={`lb-tab${tab === "xp" ? " active" : ""}`} onClick={() => setTab("xp")}>
            ⚡ CLASSEMENT XP
          </button>
          <button className={`lb-tab${tab === "duels" ? " active" : ""}`} onClick={() => setTab("duels")}>
            ⚔️ DUELS GAGNÉS
          </button>
        </div>

        {/* Top 3 podium */}
        {!loading && players.length >= 3 && (
          <div className="lb-podium">
            {/* 2nd */}
            <div className="lb-podium-slot second">
              <OniAvatar oni={players[1]?.demon?.oni} size={80} />
              <p className="lb-podium-name">{players[1]?.displayName || "—"}</p>
              <p className="lb-podium-oni">{players[1]?.demon?.oni?.name || "—"}</p>
              <div className="lb-podium-block second-block">
                <span>🥈</span>
                <span>{tab === "xp" ? players[1]?.xp : players[1]?.weeklyDuelsWon || 0}</span>
              </div>
            </div>
            {/* 1st */}
            <div className="lb-podium-slot first">
              <div className="lb-podium-crown">👑</div>
              <OniAvatar oni={players[0]?.demon?.oni} size={100} />
              <p className="lb-podium-name">{players[0]?.displayName || "—"}</p>
              <p className="lb-podium-oni">{players[0]?.demon?.oni?.name || "—"}</p>
              <div className="lb-podium-block first-block">
                <span>🥇</span>
                <span>{tab === "xp" ? players[0]?.xp : players[0]?.weeklyDuelsWon || 0}</span>
              </div>
            </div>
            {/* 3rd */}
            <div className="lb-podium-slot third">
              <OniAvatar oni={players[2]?.demon?.oni} size={80} />
              <p className="lb-podium-name">{players[2]?.displayName || "—"}</p>
              <p className="lb-podium-oni">{players[2]?.demon?.oni?.name || "—"}</p>
              <div className="lb-podium-block third-block">
                <span>🥉</span>
                <span>{tab === "xp" ? players[2]?.xp : players[2]?.weeklyDuelsWon || 0}</span>
              </div>
            </div>
          </div>
        )}

        {/* Full list */}
        {loading ? (
          <div className="lb-loading"><span className="spinner" style={{ width: 24, height: 24 }} /></div>
        ) : players.length === 0 ? (
          <EmptyState type="leaderboard_empty"/>
        ) : (
          <div className="lb-list">
            <div className="lb-list-header">
              <span>RANG</span><span>DÉMON</span><span>{tab === "xp" ? "XP TOTAL" : "DUELS GAGNÉS"}</span>
            </div>
            {players.map((p, i) => (
              <PlayerRow key={p.id} player={p} rank={i + 1} type={tab} isMe={user?.uid === p.id} />
            ))}
          </div>
        )}

        {/* How to climb */}
        <div className="lb-howto card" style={{ marginTop: "3rem" }}>
          <p className="lb-howto-title">GRIMPER AU CLASSEMENT</p>
          <div className="lb-howto-grid">
            <div className="lb-howto-item"><span>📝</span><p>+15-75 XP par entrée de journal chronifiée</p></div>
            <div className="lb-howto-item"><span>⚔️</span><p>+30 XP par duel gagné · +10 XP si perdu</p></div>
            <div className="lb-howto-item"><span>🎥</span><p>+50 XP par session coach complétée</p></div>
            <div className="lb-howto-item"><span>🔥</span><p>+50 XP pour un Oni Reform réussi</p></div>
          </div>
        </div>

      </div>
    </div>
  );
}
